// Agrega as métricas de "Ofertas Especiais" ao nível do grupo para o Dashboard: soma o
// analytics por hotel (`GET /api/v1/chat/offers/admin/analytics?hotel_id=`) com o analytics
// ao nível do grupo (`GET .../analytics/group`) — este último cobre as aberturas do
// carrossel misto de ofertas (sem hotel específico em foco na sessão, ver
// record_offers_opened no backend), que de outra forma nunca apareceriam em nenhum hotel.
//
// Limitação conhecida (mesma classe da documentada em csatMetrics.js): "sessões únicas" é
// contada por âmbito (cada hotel + grupo) e depois somada — um visitante que interaja com
// ofertas de 2 hotéis do mesmo grupo na mesma sessão conta 2x na visão agregada do grupo.

function mergeByOffer(perHotel) {
  const byOffer = new Map();
  perHotel.forEach(({ hotel, data }) => {
    (data.by_offer || []).forEach((row) => {
      const acc = byOffer.get(row.offer_id) || {
        offer_id: row.offer_id,
        offer_title: row.offer_title,
        hotel_name: hotel?.name,
        views: 0,
        clicks: 0,
        cta_clicks: 0,
        unique_viewers: 0,
        unique_clickers: 0,
        unique_cta_clickers: 0,
      };
      if (row.offer_title) acc.offer_title = row.offer_title;
      acc.views += row.views || 0;
      acc.clicks += row.clicks || 0;
      acc.cta_clicks += row.cta_clicks || 0;
      // cada oferta pertence sempre a um único hotel (ver backend record_offers_opened),
      // por isso somar sessões únicas por hotel aqui não duplica a mesma sessão 2x.
      acc.unique_viewers += row.unique_viewers || 0;
      acc.unique_clickers += row.unique_clickers || 0;
      acc.unique_cta_clickers += row.unique_cta_clickers || 0;
      byOffer.set(row.offer_id, acc);
    });
  });
  return Array.from(byOffer.values())
    .map((acc) => ({
      ...acc,
      // CTR por sessões únicas (v3 da spec, secção 14) — nunca ultrapassa 100%.
      ctr_interaction: acc.unique_viewers
        ? Math.round((acc.unique_clickers / acc.unique_viewers) * 1000) / 10
        : null,
      ctr_conversion: acc.unique_viewers
        ? Math.round((acc.unique_cta_clickers / acc.unique_viewers) * 1000) / 10
        : null,
    }))
    .sort((a, b) => b.views - a.views);
}

export async function loadOffersSummary(axiosAuth, groupId, dateFrom, dateTo, hotelId) {
  const { data: allHotels } = await axiosAuth
    .get(`/hotels/group/${groupId}`)
    .catch(() => ({ data: [] }));
  const hotels = hotelId
    ? (allHotels || []).filter((h) => String(h.id) === String(hotelId))
    : allHotels;

  const params = {};
  if (dateFrom) params.date_from = dateFrom;
  if (dateTo) params.date_to = dateTo;

  // o agregado de grupo (hotel_id=NULL, carrossel misto) só faz sentido na vista "Todos os
  // hotéis" — com um hotel específico selecionado, essas aberturas não pertencem a ele.
  const results = await Promise.allSettled([
    ...(hotels || []).map((h) =>
      axiosAuth
        .get('/chat/offers/admin/analytics', { params: { ...params, hotel_id: h.id } })
        .then(({ data }) => ({ hotel: h, data })),
    ),
    ...(hotelId
      ? []
      : [
          axiosAuth
            .get('/chat/offers/admin/analytics/group', { params: { ...params, group_id: groupId } })
            .then(({ data }) => ({ hotel: null, data })),
        ]),
  ]);

  const perHotel = results
    .filter((r) => r.status === 'fulfilled')
    .map((r) => r.value);

  if (perHotel.length === 0) {
    return { available: false };
  }

  const opened = perHotel.reduce((acc, { data }) => acc + data.opened, 0);
  const uniqueSessions = perHotel.reduce((acc, { data }) => acc + data.unique_sessions, 0);
  const viewed = perHotel.reduce((acc, { data }) => acc + data.viewed, 0);
  const clicked = perHotel.reduce((acc, { data }) => acc + data.clicked, 0);
  const ctaClicks = perHotel.reduce((acc, { data }) => acc + data.cta_clicks, 0);
  // sessões únicas (v3 da spec, secção 14) — o backend já as calcula por hotel/grupo; somar
  // aqui não duplica porque cada oferta pertence sempre a um único hotel.
  const uniqueViewers = perHotel.reduce((acc, { data }) => acc + (data.unique_viewers || 0), 0);
  const uniqueClickers = perHotel.reduce((acc, { data }) => acc + (data.unique_clickers || 0), 0);
  const uniqueCtaClickers = perHotel.reduce(
    (acc, { data }) => acc + (data.unique_cta_clickers || 0),
    0,
  );

  return {
    available: true,
    opened,
    unique_sessions: uniqueSessions,
    viewed,
    clicked,
    cta_clicks: ctaClicks,
    unique_viewers: uniqueViewers,
    unique_clickers: uniqueClickers,
    unique_cta_clickers: uniqueCtaClickers,
    // CTR por sessões únicas — nunca ultrapassa 100%, mesmo com cliques repetidos na mesma sessão.
    ctr_interaction: uniqueViewers ? Math.round((uniqueClickers / uniqueViewers) * 1000) / 10 : null,
    ctr_conversion: uniqueViewers
      ? Math.round((uniqueCtaClickers / uniqueViewers) * 1000) / 10
      : null,
    by_offer: mergeByOffer(perHotel),
  };
}
